import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { driverApi } from '../../config/driverApi';
import { 
  ArrowLeft, 
  MapPin, 
  Phone, 
  Clock, 
  Navigation,
  AlertTriangle,
  CheckCircle,
  XCircle,
  MessageSquare
} from 'lucide-react';

const DriverOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueType, setIssueType] = useState('');
  const [issueNotes, setIssueNotes] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const res = await driverApi.get(`/dashboard.php?action=order&order_id=${orderId}`);
      if (res.data.success) {
        setOrder(res.data.data);
      } else {
        alert(res.data.message || 'Order not found');
        navigate('/driver/orders');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to fetch order');
      navigate('/driver/orders');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    setUpdating(true);
    try {
      const res = await driverApi.put(`/orders.php?action=status`, {
        order_id: orderId,
        status,
      });

      if (res.data.success) {
        fetchOrder();
      } else {
        alert(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setUpdating(false);
    }
  };

  const reportIssue = async () => {
    if (!issueType) {
      alert('Please select an issue type');
      return;
    }

    setUpdating(true);
    try {
      const res = await driverApi.post(`/orders.php?action=issue`, {
        order_id: orderId,
        issue_type: issueType,
        issue_notes: issueNotes,
      });

      if (res.data.success) {
        setShowIssueModal(false);
        setIssueType('');
        setIssueNotes('');
        fetchOrder();
      } else {
        alert(res.data.message || 'Failed to report issue');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejecting the order');
      return;
    }

    setUpdating(true);
    try {
      const res = await driverApi.put(`/orders.php?action=status`, {
        order_id: orderId,
        status: 'rejected',
        reason: rejectionReason,
      });

      if (res.data.success) {
        setShowRejectModal(false);
        setRejectionReason('');
        navigate('/driver/orders');
      } else {
        alert(res.data.message || 'Failed to reject order');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Network error');
    } finally {
      setUpdating(false);
    }
  };

  const getGoogleMapsUrl = (address) => {
    if (!address) return '#';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <p>Order not found</p>
        <Link to="/driver/orders" style={{ color: '#D4AF37', textDecoration: 'none' }}>
          Back to Orders
        </Link>
      </div>
    );
  }

  const issueTypes = [
    'Customer Not Available',
    'Wrong Address',
    'Restaurant Delay',
    'Vehicle Breakdown',
    'Heavy Traffic',
    'Weather Delay',
    'Unable to Contact Customer',
    'Other',
  ];

  const getNextStatus = () => {
    const status = order.status;
    if (status === 'out_for_delivery' || status === 'ready' || status === 'assigned') return 'accepted';
    if (status === 'accepted') return 'driving_to_restaurant';
    if (status === 'driving_to_restaurant') return 'arrived_at_restaurant';
    if (status === 'arrived_at_restaurant') return 'picked_up';
    if (status === 'picked_up') return 'out_for_delivery';
    if (status === 'out_for_delivery') return 'arriving_soon';
    if (status === 'arriving_soon') return 'arrived';
    if (status === 'arrived') return 'delivered';
    return null;
  };

  const getNextStatusLabel = (status) => {
    const labels = {
      accepted: 'Accept Order',
      driving_to_restaurant: 'Start Driving to Restaurant',
      arrived_at_restaurant: 'Arrived at Restaurant',
      picked_up: 'Mark Picked Up',
      out_for_delivery: 'Start Delivery Transit',
      arriving_soon: 'Arriving Soon',
      arrived: 'Arrived at Customer',
      delivered: 'Complete Delivery',
    };
    return labels[status] || 'Update';
  };

  const nextStatus = getNextStatus();

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <Link
        to="/driver/orders"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#666',
          textDecoration: 'none',
          fontSize: '0.85rem',
          marginBottom: '16px',
        }}
      >
        <ArrowLeft size={16} />
        Back to Orders
      </Link>

      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #EAEDF1',
        marginBottom: '16px',
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
        }}>
          <div>
            <h2 style={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: '#1A1A1A',
              margin: '0 0 4px',
            }}>
              Order #{order.order_id}
            </h2>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
              {new Date(order.order_date).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          </div>
          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '0.75rem',
            fontWeight: 600,
            backgroundColor: '#D4AF3720',
            color: '#D4AF37',
            textTransform: 'capitalize',
          }}>
            {order.status.replace(/_/g, ' ')}
          </span>
        </div>

        {/* Restaurant Info */}
        <div style={{
          backgroundColor: '#FFF8E1',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid #FFE082',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#B78103',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Restaurant Information (Pickup)
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>Syra Food Point & Grill</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <MapPin size={16} color="#666" style={{ marginTop: '2px' }} />
              <span style={{ fontSize: '0.9rem', color: '#555' }}>Seguku, Entebbe Road, Kampala, Uganda</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={16} color="#666" />
              <a href="tel:+256770000001" style={{ fontSize: '0.9rem', color: '#1565C0', textDecoration: 'none', fontWeight: 500 }}>+256 770 000 001</a>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div style={{
          backgroundColor: '#FAFAFA',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#666',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Customer Information
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Phone size={16} color="#666" />
              <span style={{ fontSize: '0.9rem' }}>{order.customer_name || 'Customer'} - {order.customer_phone || 'N/A'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <MapPin size={16} color="#666" style={{ marginTop: '2px' }} />
              <span style={{ fontSize: '0.9rem' }}>{order.delivery_address || 'No address provided'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Clock size={16} color="#666" />
              <span style={{ fontSize: '0.9rem' }}>
                Order Total: UGX {Number(order.total_amount).toLocaleString()}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 600 }}>Payment:</span>
              <span style={{ fontSize: '0.9rem' }}>{order.payment_method}</span>
            </div>
          </div>
        </div>

        {/* Order Items */}
        <div style={{
          backgroundColor: '#FAFAFA',
          borderRadius: '10px',
          padding: '16px',
          marginBottom: '16px',
        }}>
          <h4 style={{
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#666',
            marginBottom: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Order Items
          </h4>
          {order.items?.map((item, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 0',
                borderBottom: i < (order.items?.length - 1) ? '1px solid #f0f0f0' : 'none',
              }}
            >
              <span style={{ fontSize: '0.85rem', color: '#333' }}>
                {item.item_name} × {item.quantity}
              </span>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                UGX {(item.price * item.quantity).toLocaleString()}
              </span>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
          {nextStatus && (
            <button
              onClick={() => updateStatus(nextStatus)}
              disabled={updating}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: updating ? '#ccc' : '#D4AF37',
                color: '#fff',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: updating ? 'not-allowed' : 'pointer',
              }}
            >
              {updating ? 'Updating...' : getNextStatusLabel(nextStatus)}
            </button>
          )}

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <a
              href={`tel:${order.customer_phone}`}
              style={{
                flex: 1,
                minWidth: '120px',
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#E3F2FD',
                color: '#1565C0',
                textAlign: 'center',
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <Phone size={16} />
              Call Customer
            </a>
            <a
              href="tel:+256770000001"
              style={{
                flex: 1,
                minWidth: '120px',
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#E8F5E9',
                color: '#2E7D32',
                textAlign: 'center',
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <Phone size={16} />
              Call Restaurant
            </a>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowIssueModal(true)}
              style={{
                flex: 1,
                minWidth: '120px',
                padding: '12px',
                borderRadius: '10px',
                backgroundColor: '#FFF3E0',
                color: '#FF9800',
                border: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                cursor: 'pointer',
              }}
            >
              <AlertTriangle size={16} />
              Report Issue
            </button>
            {['assigned', 'accepted', 'ready'].includes(order.status) && (
              <button
                onClick={() => setShowRejectModal(true)}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '12px',
                  borderRadius: '10px',
                  backgroundColor: '#FFEAEA',
                  color: '#C62828',
                  border: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                <XCircle size={16} />
                Reject Order
              </button>
            )}
          </div>

          {order.delivery_address && (
            <a
              href={getGoogleMapsUrl(order.delivery_address)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '10px',
                backgroundColor: '#4CAF50',
                color: '#fff',
                textAlign: 'center',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Navigation size={18} />
              Open Navigation
            </a>
          )}
        </div>
      </div>

      {/* Issue Modal */}
      {showIssueModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: '16px',
            }}>
              Report Delivery Issue
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#333',
                marginBottom: '8px',
              }}>
                Issue Type
              </label>
              <select
                value={issueType}
                onChange={(e) => setIssueType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Select an issue...</option>
                {issueTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#333',
                marginBottom: '8px',
              }}>
                Additional Notes
              </label>
              <textarea
                value={issueNotes}
                onChange={(e) => setIssueNotes(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '0.9rem',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowIssueModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={reportIssue}
                disabled={updating || !issueType}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: updating || !issueType ? '#ccc' : '#FF9800',
                  color: '#fff',
                  border: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: updating || !issueType ? 'not-allowed' : 'pointer',
                }}
              >
                {updating ? 'Reporting...' : 'Report Issue'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '16px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}>
            <h3 style={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: '16px',
            }}>
              Reject Delivery Assignment
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#333',
                marginBottom: '8px',
              }}>
                Reason for Rejection *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Why are you rejecting this order? (e.g. Vehicle issue, out of region)"
                rows={3}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  fontSize: '0.9rem',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={updating || !rejectionReason.trim()}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: updating || !rejectionReason.trim() ? '#ccc' : '#F44336',
                  color: '#fff',
                  border: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: updating || !rejectionReason.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {updating ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverOrderDetail;
